/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { DeviceModelDto } from 'api/deviceModels/models/DeviceModelDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Card, Container, Dropdown, DropdownButton, Form, Stack } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import styles from './DeviceModelScreen.module.scss';
import DeviceModelsService from 'api/deviceModels/DeviceModelsService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { Paged } from 'api/common/types/Page';
import { DeviceModelSearchCriteria } from 'api/deviceModels/models/DeviceModelSearchCriteria';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';

type Props = {
    version: number
};

const DeviceModelScreen: React.FC<Props> = ({ version }: Props) => {
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDeviceModelsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICE_BRAND_WRITE']);
    const { t } = useTranslation();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<DeviceModelDto>();
    const [brands, setBrands] = useState<SelectValueLabel[]>();
    const [refresh, setRefresh] = useState<number>(1);
    const initial = { id: '', name: '', deviceBrandId: '', rowVersion: '' };
    const [formData, setFormData] = useState(initial);
    const [criteria, setCriteria] = useState<DeviceModelSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'dm.name'
    });
    const [deviceModelsPage, setDeviceModelsPage] = useState<Paged<DeviceModelDto>>();
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceModelDto | null>(null);

    const showRemoveItemDialog = async (item: DeviceModelDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await DeviceModelsService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            clear();
            setCriteria({ ...criteria, page: 1 });
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device models', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    function clear() {
        setItem(initial);
        setFormData(initial);
        setValidated(false);
        setRefresh(refresh + 1);
    }

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: DeviceModelDto = {
                id: formData.id,
                name: formData.name,
                deviceBrandId: formData.deviceBrandId,
                rowVersion: formData.rowVersion,
            };
            if (model && model.id) {
                await DeviceModelsService.update(model);
            } else if (model) {
                await DeviceModelsService.create(model);
            }
            Loading.hide();
            clear();
            setCriteria({ ...criteria, page: 1, deviceBrandId: '' });
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the device model with id: ${form.id}`, error);
            toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    };

    const renderTableActionCell = (row: DeviceModelDto) => {
        return <DropdownButton id="dropdown-basic-button" title="..." size="sm" variant="outline-secondary">
            {hasDeviceModelsWritePolicy && <>
                <Dropdown.Item onClick={() => { setItem(row); setFormData({ ...row, id: row.id ?? '', rowVersion: row.rowVersion ?? '' }); }}>{t('common.edit')}</Dropdown.Item>
                <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
            </>
            }
        </DropdownButton>
    };

    useEffect(() => {
        DeviceBrandsService.getDeviceBrands()
            .then((data) => {
                setBrands(data);
            }).finally(() => { });
    }, [version]);

    useEffect(() => {
        search().catch(console.error)
    }, [criteria]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<DeviceModelDto>[] = [
        {
            field: 'name',
            name: t('config_devices.models.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'dm.name'
        },
        {
            field: 'deviceBrandName',
            name: t('config_devices.models.list.brand') ?? '',
            onSearch: onTableFilter,
            searchField: 'db.name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const search = async () => {
        try {
            Loading.show();
            const page = await DeviceModelsService.getList(criteria);
            setDeviceModelsPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device model list', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    return (<>
        <Container fluid>
            <Card className={styles.filters}>
                <Card.Body>
                    <Form onSubmit={onSubmit} noValidate validated={validated} key={item?.name}>
                        <Stack direction="horizontal" gap={3} key={refresh}>
                            <Form.Select name="deviceBrandId" defaultValue={item?.deviceBrandId}
                                onChange={(e: any) => { handleChange(e); setCriteria({ ...criteria, page: 1, deviceBrandId: e.target.value }) }}>
                                <option value={''}>{t('common.select_option')}</option>
                                {brands &&
                                    brands.map(b => <option key={b.value} value={b.value}>{b.label}</option>)
                                }
                            </Form.Select>
                            <Form.Control required name="name" type="text" defaultValue={item?.name} onChange={handleChange} placeholder={t('config_devices.models.list.name')} />
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clear}>{t('common.clear')}</Button>
                            <Button variant="primary" type="submit" onClick={onSubmit}>{t('common.save')}</Button>
                        </Stack>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
        <Container fluid className='mt-5'>
            <ListingTable
                columns={tableColumns}
                rows={deviceModelsPage?.items || []}
                allowHover={true}
                initialSearch={{ colField: 'dm.name', isOrderAsc: true }}
            />
            <PaginationWithInfo
                itemName={t('config_devices.models.title')}
                currentPage={deviceModelsPage?.currentPage ?? 1}
                pageItems={deviceModelsPage?.items.length || 0}
                totalItems={deviceModelsPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })} />
        </Container>
        <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
    </>);
};

export default DeviceModelScreen;
